import type { Guide } from "./guide";

/** Guide 7: users by your own id, a batch of asks, cost caps per user, and what each one cost. */
export const MANY_CUSTOMERS: Guide = {
  slug: "many-customers",
  samples: "many",
  title: "Serve many users at once",
  nav: "Many users",
  summary:
    "Name your users, ask them all at once, cap what each can spend, and see what each one cost.",
  minutes: 10,
  lede: "Each of your users gets a Bash of their own, named by your own id. Ask them all at once, cap what each can spend, and see what each one cost.",
  lines: ["users", "ask", "usage"],
  steps: [
    {
      id: "name",
      title: "Name your users",
      body: [
        "Bash makes a user the first time you act for them. This step adds their name and the time zone their routines run in ([`users`](/developers/users)).",
      ],
      doors: {
        ts: { sample: "guide-many-name" },
        py: { sample: "guide-many-name" },
        cli: {
          note: "The CLI names a user with `--user` on any command. Names and time zones are set from an SDK or HTTP.",
        },
        http: { sample: "guide-many-name" },
        mcp: {
          note: "An agent names a user with `user` on any tool. Names and time zones are set from an SDK or HTTP.",
        },
      },
      then: "Run it again any time: nothing is made twice, and `user.created` reaches your webhooks once.",
    },
    {
      id: "batch",
      title: "Ask them all at once",
      body: [
        "One message for many users, each in a conversation of their own ([`ask`](/developers/ask)).",
      ],
      doors: {
        ts: { sample: "guide-many-batch" },
        py: { sample: "guide-many-batch" },
        cli: { sample: "guide-many-batch" },
        http: { sample: "guide-many-batch" },
        mcp: {
          note: "Over MCP, one ask is one user. A batch is made from an SDK, the CLI or HTTP.",
        },
      },
      then: "Bash works through them within the batch's `concurrency`, and each finishes on its own.",
      prints: "Asked 2 users in bat_7xk. Follow it: vestra events bat_7xk",
    },
    {
      id: "follow",
      title: "Follow the batch",
      body: ["Each user's answer as it finishes, then the totals."],
      doors: {
        ts: { sample: "guide-many-follow" },
        py: { sample: "guide-many-follow" },
        cli: { sample: "guide-many-follow" },
        http: { sample: "guide-many-follow" },
        mcp: { note: "Batches are followed from an SDK, the CLI or HTTP." },
      },
      then: "An item's `result.text` is its work's answer. `batch.completed` reaches your webhooks once every item has finished.",
      prints: [
        "  traveller-42: succeeded: Your flight 6E 2134 is on time.",
        "  traveller-77: succeeded: AI 865 leaves 40 minutes late.",
        "bat_7xk: completed. 2 succeeded, 0 failed; $0.0610.",
      ].join("\n"),
    },
    {
      id: "cap",
      title: "Cap what each can spend",
      body: [
        "A monthly cap for each user, and alerts on the way ([`usage`](/developers/usage)).",
      ],
      doors: {
        ts: { sample: "guide-many-cap" },
        py: { sample: "guide-many-cap" },
        cli: { sample: "guide-many-cap" },
        http: { sample: "guide-many-cap" },
        mcp: {
          note: "Caps stay with people: set them from an SDK, the CLI or HTTP.",
        },
      },
      then: "A user at their cap is refused new work with `402 user_cap_reached`. `usage.threshold_reached` and `usage.cap_reached` reach your webhooks.",
    },
    {
      id: "spend",
      title: "See what each spent",
      body: ["Billed US dollars, grouped by user."],
      doors: {
        ts: { sample: "guide-many-spend" },
        py: { sample: "guide-many-spend" },
        cli: { sample: "guide-many-spend" },
        http: { sample: "guide-many-spend" },
        mcp: { sample: "usage" },
      },
      then: "One row per user, and the total. Line items are the receipt behind each one.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: ["Name, cap, ask and count, in one program."],
      doors: {
        ts: { sample: "guide-many-whole" },
        py: { sample: "guide-many-whole" },
        cli: { sample: "guide-many-whole" },
        http: { sample: "guide-many-whole" },
        mcp: {
          note: "An agent reads spend with the `usage` tool. Batches and caps are set from an SDK, the CLI or HTTP.",
        },
      },
      then: "Every user answered in their own conversation, inside the cap you set.",
    },
  ],
};
